import DocumentIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Document.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { useSelector } from 'react-redux';
import { UploadArea } from 'src/components/atlas/molecules';
import { WYSIWYG } from 'src/components/atomsLegacy';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import { CaseByIdQuery, UploadSource } from 'src/graphql';
import {
  UseCaseDocuments,
  UseCreateDocument,
  useCreateDocument,
} from 'src/hooks/cases';
import { UploadScanStatus } from 'src/types';
import { validateFileUpload } from 'src/utils/validateFileUpload/fileValidation';
import { useTheme as useStyledTheme } from 'styled-components';
import { DrawerShell } from '../../molecules/Drawer/DrawerShell';
import * as styles from './WorkspaceDocumentEditor.styles';

interface Props {
  caseId: string;
  caseDocuments: Omit<
    UseCaseDocuments,
    'activeTab' | 'setTempActiveTab' | 'selectActiveTab'
  >;
  isSaving: boolean;
  setIsSaving: (isSaving: boolean) => void;
  refetch: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>;
  disableLegacyStyles?: boolean;
  open: boolean;
  onClosed: () => void;
  isReadOnly?: boolean;
}

export const WorkspaceDocumentEditor: FC<Props> = ({
  caseId,
  caseDocuments,
  isSaving,
  setIsSaving,
  refetch,
  disableLegacyStyles,
  open,
  onClosed,
  isReadOnly = false,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const styledTheme = useStyledTheme();
  const sp = tokens.semantic.spacing.desktop.gap;
  const color = tokens.semantic.color;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<
    UseCreateDocument['uploads'][number] | null
  >(null);

  const createDocument = useCreateDocument(
    caseId,
    caseDocuments.openDocument,
    caseDocuments.setDocumentState,
    refetch,
  );

  const exitEditor = useCallback(() => {
    createDocument.setDocumentHasChanged(false);
    caseDocuments.setOpenDocumentId(null);
    caseDocuments.closeDocumentEditor();
    setIsSaving(false);
    onClosed();
  }, [createDocument, caseDocuments, setIsSaving, onClosed]);

  const [invalidFiles, setInvalidFiles] = useState<
    Array<{ name: string; type: string }>
  >([]);

  // Preserve scan statuses during save operations to prevent color flickering
  const [preservedScanStatuses, setPreservedScanStatuses] = useState<
    Record<string, UploadScanStatus>
  >({});

  // Update preserved statuses when uploads change (but not during save)
  useEffect(() => {
    if (!isSaving && createDocument.uploads) {
      const statusMap: Record<string, UploadScanStatus> = {};
      createDocument.uploads.forEach((upload) => {
        if (upload.s3path && upload.scanStatus) {
          statusMap[upload.s3path] = upload.scanStatus as UploadScanStatus;
        }
      });
      setPreservedScanStatuses(statusMap);
    }
  }, [createDocument.uploads, isSaving]);

  const showInvalidFilesToast = useCallback(() => {
    const fileNames = invalidFiles.map((file) => file.name).join(', ');
    handleErrorToast({
      toastMessage: 'Unsupported file types',
      toastDescription: `The following files have unsupported file types: ${fileNames}`,
    });
    setInvalidFiles([]);
  }, [invalidFiles]);

  useEffect(() => {
    if (invalidFiles.length > 0) {
      const timer = setTimeout(showInvalidFilesToast, 100);
      return () => clearTimeout(timer);
    }
  }, [invalidFiles, showInvalidFilesToast]);

  const [isUploading, setIsUploading] = useState(false);

  const reduxCase = useSelector(casePageVm);

  const userHasEditAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  // Upload each valid file through the existing document upload processor and
  // append the successful ones to the document's uploads list.
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      const MAX_FILE_BYTES = 25 * 1024 * 1024;
      const MAX_TOTAL_BYTES = 100 * 1024 * 1024;

      const oversized = files.find((f) => f.size > MAX_FILE_BYTES);
      if (oversized) {
        handleErrorToast({
          toastMessage: 'File too large',
          toastDescription: `"${oversized.name}" exceeds the 25 MB per-file limit.`,
        });
        return;
      }

      const existingTotal = createDocument.uploads.reduce(
        (sum, u) => sum + (u.size ?? 0),
        0,
      );
      const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
      if (existingTotal + incomingTotal > MAX_TOTAL_BYTES) {
        handleErrorToast({
          toastMessage: 'Total size exceeded',
          toastDescription: 'Total upload size cannot exceed 100 MB.',
        });
        return;
      }

      const invalid: Array<{ name: string; type: string }> = [];
      const failed: Array<{ name: string; error: string }> = [];
      const newlyUploaded: Parameters<typeof createDocument.setUploads>[0] = [];

      for (const file of files) {
        try {
          validateFileUpload({ filename: file.name, mimeType: file.type });
        } catch (error) {
          invalid.push({ name: file.name, type: file.type });
          continue;
        }

        const alreadyAdded =
          createDocument.uploads.some((u) => u.filename === file.name) ||
          newlyUploaded.some((u) => u.filename === file.name);
        if (alreadyAdded) continue;

        const committedTotal =
          existingTotal +
          newlyUploaded.reduce((sum, u) => sum + (u.size ?? 0), 0);
        if (committedTotal + file.size > MAX_TOTAL_BYTES) {
          failed.push({
            name: file.name,
            error: 'Total upload size cannot exceed 100 MB.',
          });
          continue;
        }

        setIsUploading(true);
        const s3path = await new Promise<string | null>((resolve) => {
          createDocument.uploadFile(
            file.name,
            file,
            {},
            (serverId) =>
              resolve(typeof serverId === 'string' ? serverId : null),
            (errorText) => {
              failed.push({ name: file.name, error: errorText });
              resolve(null);
            },
            () => {},
            () => {
              failed.push({ name: file.name, error: 'Upload aborted' });
              resolve(null);
            },
            () => {},
            {
              chunkTransferId: '',
              chunkServer: { url: '' },
              chunkUploads: false,
              chunkForce: false,
              chunkSize: 0,
              chunkRetryDelays: [],
            },
          );
        });
        setIsUploading(false);

        if (s3path != null) {
          newlyUploaded.push({
            s3path,
            filename: file.name,
            size: file.size,
            scanStatus: UploadScanStatus.PENDING,
            uploadSource: UploadSource.ReshubWorkspace,
          });
        }
      }

      if (invalid.length > 0) {
        setInvalidFiles((prev) => [...prev, ...invalid]);
      }
      if (failed.length > 0) {
        failed.forEach(({ name, error }) => {
          console.error('Workspace document upload failed', {
            caseId,
            filename: name,
            error,
          });
        });
        handleErrorToast({
          toastMessage: 'Upload failed',
          toastDescription: `The following files failed to upload: ${failed
            .map(({ name }) => name)
            .join(', ')}`,
        });
      }
      if (newlyUploaded.length > 0) {
        createDocument.setUploads([
          ...createDocument.uploads,
          ...newlyUploaded,
        ]);
      }
    },
    [createDocument, caseId],
  );

  const handleCloseEditor = useCallback(() => {
    exitEditor();
  }, [exitEditor]);

  useEffect(() => {
    if (!createDocument.documentHasChanged && isSaving) {
      caseDocuments.setActiveTab(caseDocuments.tempActiveTab);
      caseDocuments.setOpenDocumentId(null);
      caseDocuments.setCreateDocumentEditorOpen(false);
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDocuments, createDocument.documentHasChanged, isSaving]);

  return (
    <>
      <DrawerShell
        testId="case-document-editor"
        open={open}
        title={isReadOnly ? createDocument.title : 'Add new document'}
        onRequestClose={handleCloseEditor}
        onNext={async () => {
          await caseDocuments.handleSaveDocument(
            createDocument.title,
            createDocument.value,
            createDocument.uploads.map((upload) => upload.s3path),
          );
          createDocument.setDocumentHasChanged(false);
          caseDocuments.setCreateDocumentEditorOpen(false);
          exitEditor();
        }}
        nextDisabled={
          !userHasEditAccess || !createDocument.documentHasChanged || isReadOnly
        }
        nextTitle={'Save'}
      >
        {!isReadOnly && (
          <Box>
            <TextField
              fullWidth
              value={createDocument.title}
              onChange={(e) => createDocument.setTitle(e.target.value)}
              placeholder="Enter text"
              label="Document name"
            />
          </Box>
        )}

        {!isReadOnly ? (
          <Box>
            <Typography variant="body2" sx={styles.label}>
              Document text
            </Typography>
            <Box
              sx={{
                border: '1px solid black',
                borderRadius: '8px',
                padding: '2px',
              }}
            >
              <WYSIWYG
                key={caseDocuments.openDocument?.id ?? 'new-document'}
                onChange={(value) => {
                  createDocument.setValue(value.markdown);
                }}
                value={caseDocuments.openDocument?.value ?? ''}
                placeholder="Type or paste your text here"
                disabled={!userHasEditAccess}
                framed={disableLegacyStyles}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: tokens.semantic.radius.lg.value,
              background: tokens.semantic.color.surface.variant.value,
              padding: sp[2].value,
            }}
          >
            <Box
              data-testid="document-text"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: tokens.semantic.radius.lg.value,
                background: tokens.semantic.color.background.base.value,
                padding: sp[2].value,
                minHeight: '300px',
              }}
            >
              <WYSIWYG
                key={caseDocuments.openDocument?.id ?? 'new-document'}
                value={caseDocuments.openDocument?.value ?? ''}
                readOnly
                framed={disableLegacyStyles}
              />
            </Box>
          </Box>
        )}

        <Box>
          <Typography variant="body2" sx={styles.label}>
            Additional documents
          </Typography>
          <Box>
            {userHasEditAccess && !isReadOnly && (
              <UploadArea
                isUploading={isUploading}
                onFilesSelected={handleFilesSelected}
                maxFileSizeLabel="Max. file size: 25 MB / 100 MB in total for all files"
              />
            )}

            {createDocument.uploads && createDocument.uploads.length > 0 && (
              <Box
                sx={{
                  marginTop: sp[2].value,
                  borderRadius: '8px',
                  border: `1px solid ${styledTheme.atlas.semantic.color.ui.divider.default}`,
                  padding: sp[2].value,
                }}
              >
                {createDocument.uploads.map((uploadedFile) => {
                  const currentScanStatus =
                    uploadedFile.scanStatus as UploadScanStatus;
                  const preservedStatus =
                    preservedScanStatuses[uploadedFile.s3path];
                  const scanStatus =
                    currentScanStatus ||
                    (isSaving && preservedStatus
                      ? preservedStatus
                      : UploadScanStatus.PENDING);

                  return (
                    <Box
                      data-testid={`document-upload-${uploadedFile.s3path}-row`}
                      key={uploadedFile.s3path}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        '&:not(:last-child)': {
                          borderBottom: `1px solid ${styledTheme.atlas.semantic.color.ui.divider.default}`,
                          paddingBottom: sp[2].value,
                        },
                        '&:not(:first-child)': {
                          paddingTop: sp[2].value,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color:
                            styledTheme.atlas.semantic.color.status.neutral
                              .text,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                        onClick={() =>
                          createDocument.handleOpenFile(uploadedFile)
                        }
                      >
                        <DocumentIcon />
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              styledTheme.atlas.semantic.color.type.default,
                            fontSize: '16px',
                          }}
                        >
                          {uploadedFile.filename}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              styledTheme.atlas.semantic.color.type.default,
                            fontSize: '16px',
                          }}
                        >
                          {scanStatus}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: sp[2].value,
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            createDocument.handleOpenFile(uploadedFile)
                          }
                        >
                          View
                        </Typography>
                        {!isReadOnly && (
                          <Typography
                            data-testid="trash-icon"
                            variant="body2"
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(uploadedFile);
                              setShowConfirmDialog(true);
                            }}
                          >
                            <TrashIcon />
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </DrawerShell>
      <Dialog
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
        }}
      >
        <DialogTitle>Delete file</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to delete this file. <br /> If you continue, all
            changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            data-testid="cancel-remove-document-confirm"
            variant="outlined"
            size="medium"
            onClick={() => {
              setShowConfirmDialog(false);
              setSelectedFile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid="confirm-remove-documents"
            sx={{
              backgroundColor: color.action.destructive.default.value,
              color: color.action.destructive.onDestructive.value,
              '&:not(.Mui-disabled):hover': {
                backgroundColor: color.action.destructive.hover.value,
              },
            }}
            variant="contained"
            size="medium"
            onClick={() => {
              if (selectedFile) {
                createDocument.handleOnRemoveFile(selectedFile);
              }
              setShowConfirmDialog(false);
              setSelectedFile(null);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
